import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useLocation, useParams } from 'react-router-dom';
import {
  apiErrorText,
  useListPtoVolumesQuery,
  useExtractPtoVolumesMutation,
  useAnnotatePtoVolumeMutation,
  useLazyExportPtoMaterialsVedomostQuery,
  useGetPtoRevisionQuery,
  useGetPtoVolumeSelectionQuery,
  useGetPtoVolumesSummaryQuery,
  useListPtoRdDocumentsQuery,
  useCreateAutoPtoSqFromRdMutation,
  invalidatePtoVolumeEdits,
  type PtoVolumePosition,
} from '@shared/api';
import { useAppDispatch } from '@app/hooks';
import { useAgentPermission } from '@entities/user';
import { SendToSupplyDialog } from '@features/request-supply';
import { RevisionFlagsBanner } from './RevisionFlagsBanner';
import { RdGapsPanel } from './rdGaps/RdGapsPanel';
import { useRdGapsScreen } from './rdGaps/useRdGapsScreen';
import { noticeText, useRdSheetOpener, useSheetVolumeChanged } from './rdSheet/opener';
import {
  anchorNote,
  findAnchoredPosition,
  parsePositionAnchor,
  volumeRowDomId,
} from './volumes/rowRef';
import type { VolumeCellContext } from './volumes/columns';
import { hasGroups, rdPositions, withoutGroupHeadings } from './volumes/groups';
import { GroupsTable } from './volumes/GroupsTable';
import { sheetTargetFor } from './volumes/SelectionCells';
import { Muted } from './volumes/styles';
import { SummaryTable } from './volumes/SummaryTable';
import {
  defaultView,
  hasSystems,
  viewForLinkedRow,
  type VolumesView,
} from './volumes/systems';
import { SystemsRegistry } from './volumes/SystemsRegistry';
import { SystemsTable } from './volumes/SystemsTable';
import {
  GAPS_TAB,
  activeScreenTab,
  defaultTab,
  filterVolumes,
  marksById,
  partTabOf,
  revealFor,
  tabContext,
  tabStats,
  tabTitle,
  volumeCounters,
  type ScreenTab,
} from './volumes/tabs';
import { ViewSwitch } from './volumes/ViewSwitch';
import { VolumesHeader } from './volumes/VolumesHeader';
import { VolumesTable } from './volumes/VolumesTable';
import { VolumesTabs } from './volumes/VolumesTabs';
import { VolumesToolbar } from './volumes/VolumesToolbar';
import { RenumberButton } from './rdEdits/RenumberButton';
import { RevisionEditsPanel } from './rdEdits/RevisionEditsPanel';
import { RowEditDialog, type RowEditAction } from './rdEdits/RowEditDialog';
import { useRdVolumeEditAccess } from './rdEdits/useRdVolumeEditAccess';

const Note = styled(Muted)`
  margin-bottom: 12px;
`;

const ErrorText = styled(Muted)`
  color: crimson;
`;

const Dismiss = styled.button`
  margin-left: 8px;
  padding: 0 4px;
  border: 0;
  background: none;
  font: inherit;
  color: inherit;
  cursor: pointer;
`;

/** Экран «Позиции объёма и входной контроль РД» ревизии: данные, права и действия.
 *  Части экрана — в `./volumes` (графы таблицы — `volumes/columns.tsx`). Открытие
 *  по ссылке на строку (`#pos=<часть>.<№>&uid=<record_uid>`) прокручивает к строке
 *  и подсвечивает её.
 *
 *  Виды (выпуск 5, замечание №3): «По системам» (по умолчанию в ОВ, ВК, НВ, НК —
 *  система ▶ раздел ▶ строки в порядке РД с номерами; в прочих комплектах — «По
 *  группам»: вложенные уровни групп таблиц, замечание 23.10, п. 3), «Сводно ▶»
 *  (ведомость материалов ▶ доли систем или групп) и «Строки по
 *  порядку РД» (по умолчанию в прочих). Виды со строками — с вкладками по части
 *  РД («Материалы (СП01/СО)», «Объёмы работ (ВР01)», «Прочее»; металлопрокат,
 *  сводные и — в комплекте без СП/СО — спецификации листов чертежей в
 *  «Материалах», работы по чертежам — в «Объёмах работ»), «скрыть повторы
 *  вне суммы» и счётчиками — по признакам того же отбора, что и ведомость
 *  материалов (`…/volume-selection`). Счётчик «есть в ВОР, нет в СП» (выпуск 2)
 *  открывает список этих строк с кандидатом пары из СП. Номер строки, «Источник»
 *  и отметки «✓ / ⚠» в «Сверке с листами РД» открывают лист в окне исходного
 *  листа РД (`./rdSheet`). Правки РД «Инженер+» (№8) — в `./rdEdits`: окно
 *  правки строки, правки ревизии, ждущие решения, «Перенумеровать».
 *
 *  Анализ комплектности РД (№7, `./rdGaps`) — последней вкладкой видов со
 *  строками и значками «⚠ N» у строк ВОР, если он включён для проекта и роли;
 *  экран только передаёт данные и переключает вкладку. */
export function PtoVolumes() {
  const { projectId = '', revisionId = '' } = useParams();
  const { hash } = useLocation();
  const { can_write } = useAgentPermission('pto');
  const rdEdits = useRdVolumeEditAccess(projectId);
  const [editing, setEditing] = useState<{ id: string; action: RowEditAction } | null>(null);
  const [annotateError, setAnnotateError] = useState<string | null>(null);
  const { data, isLoading, isError } = useListPtoVolumesQuery(revisionId, {
    skip: !revisionId,
  });
  const selection = useGetPtoVolumeSelectionQuery(revisionId, { skip: !revisionId });
  // Выбранная вкладка — своя у каждой ревизии (экран не пересоздаётся при
  // переходе на другую ревизию); не выбрана — по умолчанию (`defaultTab`).
  const [tabPick, setTabPick] = useState<{ revisionId: string; tab: ScreenTab } | null>(null);
  const tab = tabPick?.revisionId === revisionId ? tabPick.tab : null;
  // Список «Есть в ВОР, нет в СП» (выпуск 2) — вместо вкладки, своей ревизии.
  const [vorPick, setVorPick] = useState<string | null>(null);
  const vorOnly = vorPick === revisionId && !!revisionId;
  const setVorOnly = (on: boolean) => setVorPick(on ? revisionId : null);
  const setTab = (t: ScreenTab) => {
    setTabPick({ revisionId, tab: t });
    setVorPick(null);
  };
  // Вид списка (выпуск 5): выбор инженера своей ревизии; пока не выбран — по
  // разметке систем ревизии (`defaultView`).
  const [viewPick, setViewPick] = useState<{ revisionId: string; view: VolumesView } | null>(
    null,
  );
  const setView = (v: VolumesView) => setViewPick({ revisionId, view: v });
  // Анализ комплектности (№7): флаг для проекта и роли, значки «⚠ N».
  const gaps = useRdGapsScreen(projectId, revisionId);
  const [hideRepeats, setHideRepeats] = useState(true);
  const sheet = useRdSheetOpener();
  const [extract, { isLoading: extracting }] = useExtractPtoVolumesMutation();
  const [annotate] = useAnnotatePtoVolumeMutation();
  const [exportVedomost, { isFetching: exportingVed }] = useLazyExportPtoMaterialsVedomostQuery();
  const [createAutoSq, { isLoading: creatingAutoSq }] = useCreateAutoPtoSqFromRdMutation();
  const [sqNote, setSqNote] = useState<string | null>(null);
  const [onlyReview, setOnlyReview] = useState(false);
  const [toSupply, setToSupply] = useState(false);
  // Шифр документа ревизии и номер ревизии — в шапку, текст ссылки на строку и
  // название заявки в снабжение. Оба запроса доступны и читателю.
  const { data: revData } = useGetPtoRevisionQuery(revisionId, { skip: !revisionId });
  const { data: docsData } = useListPtoRdDocumentsQuery({ projectId }, { skip: !projectId });
  const docId = revData?.revision.document_id ?? undefined;
  const docCode = docsData?.items.find((d) => d.id === docId)?.code ?? null;

  const items = data?.items;
  // Строка-заголовок группы (проход «Обновить разделы», `raw.group_heading`)
  // строкой не выводится и в счётчики не идёт.
  const all: PtoVolumePosition[] = useMemo(() => withoutGroupHeadings(items ?? []), [items]);
  const systemsOn = useMemo(() => hasSystems(all), [all]);
  // Группы таблиц (комплекты без систем): графа «Группа», вид «По группам».
  const groupsOn = useMemo(() => !systemsOn && hasGroups(all), [all, systemsOn]);
  // «Поз.» РД в графе «Шифр» (вопрос 10, вариант «б»).
  const rdPos = useMemo(() => rdPositions(all), [all]);
  const view: VolumesView =
    (viewPick?.revisionId === revisionId ? viewPick.view : null) ?? defaultView(all);
  const reviewCount = all.filter((p) => p.needs_review).length;
  const tableCount = all.filter((p) => p.source_role === 'rd_table').length;
  // «Сводно ▶»: строки ведомости на проверке — для счётчика фильтра (тот же
  // запрос, что у таблицы вида).
  const summary = useGetPtoVolumesSummaryQuery(revisionId, {
    skip: !revisionId || view !== 'summary',
  });
  const summaryReview = summary.data?.rows.filter((r) => r.needs_review).length ?? 0;

  // Признаки отбора ведомости (в сумме / повтор / сверка) и вкладки по части РД.
  // Признаки не пришли (сбой, старый сервер) — повторов не знаем и не прячем.
  const marks = useMemo(() => marksById(selection.data?.items), [selection.data]);
  const byId = useMemo(() => new Map(all.map((p) => [p.id, p])), [all]);
  // Ревизия без частей СП/СО (АР1, АР2, КМ2) — «Материалы (спецификации на
  // листах)»; работы по чертежам — на вкладке «Объёмы работ» (замечание 23.10).
  const tabCtx = useMemo(() => tabContext(all, marks), [all, marks]);
  const stats = useMemo(
    () => tabStats(all, marks, hideRepeats, tabCtx),
    [all, marks, hideRepeats, tabCtx],
  );
  const repeats = useMemo(() => tabStats(all, marks, true, tabCtx), [all, marks, tabCtx]);
  // Правила выпуска 2 («СП01 основной, ВР не в материалах»): счётчики — по
  // признакам бэкенда, как строки Excel.
  const specOnly = selection.data?.spec_only === true;
  const vorNotInSpec = selection.data?.counts.vor_not_in_spec ?? null;
  // Строк ведомости материалов, как в Excel («в ведомости 584 строки (1 332
  // позиции)»); без правил выпуска 2 — счётчик позиций выпуска 1б.
  const vedomostRows = selection.data?.counts.vedomost_rows ?? null;
  const counters = useMemo(
    () => volumeCounters(all, marks, { specOnly, vorNotInSpec, vedomostRows }),
    [all, marks, specOnly, vorNotInSpec, vedomostRows],
  );
  const activeTab = activeScreenTab(tab, stats, gaps.enabled);
  // Часть РД открытой вкладки; на «Анализе комплектности» — null.
  const partTab = partTabOf(activeTab);
  const showVor = vorOnly && specOnly;
  // Вкладка части РД для фильтра строк. Список «Есть в ВОР, нет в СП» вкладку
  // не учитывает (строки со всех вкладок) — и поверх «Анализа комплектности».
  const listTab = partTab ?? defaultTab(stats);
  const positions = useMemo(
    () =>
      partTab || showVor
        ? filterVolumes(all, marks, {
            tab: listTab,
            hideRepeats,
            onlyReview,
            vorOnly: showVor,
            ctx: tabCtx,
          })
        : [],
    [all, marks, partTab, listTab, hideRepeats, onlyReview, showVor, tabCtx],
  );
  const vorReview = useMemo(
    () =>
      showVor
        ? filterVolumes(all, marks, {
            tab: listTab,
            hideRepeats: false,
            onlyReview: true,
            vorOnly: true,
          }).length
        : 0,
    [all, marks, listTab, showVor],
  );
  const marksReady = !!selection.data || selection.isError || !revisionId;

  // Строка из ссылки — по полному списку ревизии, а не по отфильтрованному.
  const anchor = useMemo(() => parsePositionAnchor(hash), [hash]);
  const found = useMemo(
    () => (anchor && items ? findAnchoredPosition(items, anchor) : null),
    [anchor, items],
  );
  const targetId = found?.position.id ?? null;
  const linkNote = anchor && items ? anchorNote(anchor, found) : null;

  // Один раз на якорь: строку показать — открыть её вкладку, снять «Только на
  // проверку» и «скрыть повторы», если они её прячут, — и прокрутить к ней. В
  // «Сводно ▶» строк РД нет — к строке ведёт вид со строками по правилу вида по
  // умолчанию (`viewForLinkedRow`). Ждём признаков
  // отбора: без них не знаем, повтор ли строка. Перезапрос списка после
  // разметки прокрутку не повторяет.
  const revealedFor = useRef<string | null>(null);
  const scrollTo = useRef<string | null>(null);
  useEffect(() => {
    const key = `${revisionId}${hash}`;
    if (!found || !marksReady || revealedFor.current === key) return;
    revealedFor.current = key;
    if (!found.position.needs_review) setOnlyReview(false);
    if (view === 'summary') setView(viewForLinkedRow(view, all));
    const r = revealFor(found.position, marks, tabCtx);
    setTab(r.tab);
    if (r.showRepeats) setHideRepeats(false);
    scrollTo.current = found.position.id;
  }, [found, hash, revisionId, marks, marksReady, view, all, tabCtx]);
  useEffect(() => {
    if (!scrollTo.current) return;
    const row = document.getElementById(volumeRowDomId(scrollTo.current));
    if (!row) return; // строка появится после снятия фильтра — следующая отрисовка
    scrollTo.current = null;
    row.scrollIntoView({ block: 'center' });
  });

  /** Лист строки — в окне исходного листа РД (одно окно на все клики). */
  function onOpenSheet(p: PtoVolumePosition) {
    const target = sheetTargetFor(p, docCode, all);
    if (target) sheet.open(target);
  }

  /** Значок «⚠ N» строки ВОР — вкладка анализа с находками этой строки. */
  function onGapRow(recordUid: string) {
    gaps.showRow(recordUid);
    setTab(GAPS_TAB);
  }

  // Строку исправили в панели окна листа (№8): у окна свой кэш — позиции и
  // правки этой ревизии перезапрашиваем.
  const dispatch = useAppDispatch();
  const onSheetChanged = useCallback(
    (changed: string) => {
      if (changed === revisionId) dispatch(invalidatePtoVolumeEdits());
    },
    [dispatch, revisionId],
  );
  useSheetVolumeChanged(onSheetChanged);

  async function onAnnotate(id: string, decision: string) {
    setAnnotateError(null);
    try {
      await annotate({ id, decision }).unwrap();
    } catch (e) {
      // С правками РД (№8) решение может быть недоступно роли («Откл.» на строке
      // с количеством — только «Инженер+») или позиция закрыта — объясняем.
      setAnnotateError(apiErrorText(e, 'Решение по строке не сохранено.'));
    }
  }

  // Строка окна правки — из свежего списка (после правки он перезапрашивается).
  const editingPos = editing ? (all.find((p) => p.id === editing.id) ?? null) : null;
  const revision = revData?.revision ?? null;
  const revisionEditable = revision?.status === 'active' && revision.ingest_status === 'indexed';

  async function onDownloadVedomost() {
    try {
      const blob = await exportVedomost(revisionId).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `materials_vedomost_${revisionId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  }

  async function onCreateSqFromRd() {
    setSqNote(null);
    try {
      const res = await createAutoSq({ projectId, document_id: docId }).unwrap();
      setSqNote(
        `Сформирован запрос SQ «${res.request.subject}» (пунктов замечаний: ${res.issues_count}). Перейдите в «Изменения и SQ».`,
      );
    } catch (e) {
      setSqNote(apiErrorText(e, 'Не удалось сформировать запрос SQ по замечаниям РД.'));
    }
  }

  const ctx: VolumeCellContext = {
    canWrite: can_write,
    onAnnotate,
    docCode,
    projectId,
    revisionId,
    marks,
    byId,
    onOpenSheet,
    hasSystems: systemsOn,
    hasGroups: groupsOn,
    rdPos,
    gaps: gaps.enabled
      ? { flags: gaps.flags, onOpen: onGapRow, estimates: gaps.estimates }
      : null,
    rdEdits,
    onOpenEdit: (p, action) => setEditing({ id: p.id, action }),
  };
  const isSummary = view === 'summary';

  return (
    <div>
      <VolumesHeader
        projectId={projectId}
        docCode={docCode}
        revision={revData?.revision ?? null}
        total={all.length}
        tableCount={tableCount}
        reviewCount={reviewCount}
      />

      <RevisionFlagsBanner revisionId={revisionId} />

      <VolumesToolbar
        canWrite={can_write}
        total={all.length}
        reviewCount={reviewCount}
        extracting={extracting}
        onExtract={() => extract(revisionId)}
        exportingVed={exportingVed}
        onDownloadVedomost={onDownloadVedomost}
        onOpenSupply={() => setToSupply(true)}
        creatingAutoSq={creatingAutoSq}
        onCreateSqFromRd={onCreateSqFromRd}
        onlyReview={onlyReview}
        onOnlyReviewChange={setOnlyReview}
        tabReviewCount={
          isSummary ? summaryReview : showVor ? vorReview : partTab ? stats[partTab].review : null
        }
        hideRepeats={selection.data && !isSummary && (partTab || showVor) ? hideRepeats : null}
        onHideRepeatsChange={setHideRepeats}
        repeatCount={partTab ? repeats[partTab].hidden : 0}
        extra={
          <RenumberButton revisionId={revisionId} rights={rdEdits} disabled={!revisionEditable} />
        }
      />
      {sqNote && <Note>{sqNote}</Note>}
      {linkNote && <Note role="status">{linkNote}</Note>}
      {noticeText(sheet.notice) && (
        <Note role="alert">
          {noticeText(sheet.notice)}
          <Dismiss type="button" aria-label="Скрыть" onClick={sheet.dismiss}>
            ×
          </Dismiss>
        </Note>
      )}
      {annotateError && <ErrorText role="alert">{annotateError}</ErrorText>}
      <RevisionEditsPanel
        projectId={projectId}
        revisionId={revisionId}
        positions={all}
        rights={rdEdits}
      />
      {all.length > 0 && <ViewSwitch view={view} onChange={setView} systems={systemsOn} />}
      {systemsOn && <SystemsRegistry revisionId={revisionId} />}

      {isLoading ? (
        <Muted>Загрузка…</Muted>
      ) : isError ? (
        <ErrorText>Не удалось загрузить позиции.</ErrorText>
      ) : all.length === 0 ? (
        <Muted>
          Позиции ещё не извлечены. Дождитесь автоизвлечения после индексации ревизии
          {can_write ? ' или нажмите «Извлечь заново».' : '.'}
        </Muted>
      ) : isSummary ? (
        <SummaryTable revisionId={revisionId} onlyReview={onlyReview} />
      ) : (
        <>
          <VolumesTabs
            tab={activeTab}
            onTab={setTab}
            ctx={tabCtx}
            stats={stats}
            counters={counters}
            vorOnly={showVor}
            onVorOnly={setVorOnly}
            gapsTab={gaps.enabled}
          />
          {showVor && (
            <Note role="status">
              Есть в ВОР, нет в СП — строки ведомости объёмов работ, похожие на материалы, без пары
              в спецификации ({counters?.vorNotInSp ?? 0}): в перечень материалов и снабжение они не
              входят — проверьте по РД. Кандидат пары из СП — в графе «Сверка с листами РД», как на
              листе «Сверка (ЧТЖ и ВР)» Excel.
              <Dismiss
                type="button"
                aria-label="Показать вкладку"
                onClick={() => setVorOnly(false)}
              >
                ×
              </Dismiss>
            </Note>
          )}
          {activeTab === GAPS_TAB && !showVor ? (
            <RdGapsPanel
              projectId={projectId}
              revisionId={revisionId}
              documentId={docId}
              docCode={docCode}
              canWrite={can_write}
              canSaveRules={gaps.canSaveRules}
              mode={gaps.mode}
              rowUid={gaps.rowUid}
              onClearRow={gaps.clearRow}
              onOpenSheet={sheet.open}
            />
          ) : (
            <>
              {selection.isError && (
                <Note>
                  Признаки «в сумме / повтор» не загрузились — повторы не спрятаны, графа сверки
                  пуста.
                </Note>
              )}
              {positions.length === 0 && showVor ? (
                <Muted>Строк «Есть в ВОР, нет в СП»{onlyReview ? ' на проверке' : ''} нет.</Muted>
              ) : positions.length === 0 ? (
                <Muted>
                  На вкладке «{tabTitle(listTab, tabCtx)}» строк нет
                  {onlyReview ? ' на проверке' : ''}
                  {hideRepeats && repeats[listTab].hidden > 0
                    ? ` — повторы вне суммы (${repeats[listTab].hidden}) спрятаны.`
                    : '.'}
                </Muted>
              ) : view === 'systems' && systemsOn ? (
                <SystemsTable positions={positions} ctx={ctx} targetId={targetId} />
              ) : view === 'systems' ? (
                <GroupsTable positions={positions} ctx={ctx} targetId={targetId} />
              ) : (
                <VolumesTable positions={positions} ctx={ctx} targetId={targetId} />
              )}
            </>
          )}
        </>
      )}

      {editingPos && editing && (
        <RowEditDialog
          key={`${editing.id}:${editing.action}`}
          position={editingPos}
          rights={rdEdits}
          initial={editing.action}
          onClose={() => setEditing(null)}
        />
      )}

      {toSupply && (
        <SendToSupplyDialog
          projectId={projectId}
          revisionId={revisionId}
          docCode={docCode}
          onClose={() => setToSupply(false)}
        />
      )}
    </div>
  );
}
